import toast from "react-hot-toast";
import { z } from "zod";

import { createAxiosInstance } from "api/axiosConfig";

export const medicineSchema = z
  .object({
    dosage: z.string().min(1, { message: "Please provide dosage." }),
    form: z.string().min(1, { message: "Please provide form." }),
    startDate: z.string().min(1, { message: "Please provide start date." }),
    finishDate: z.string().optional(),
    endDate: z.string().optional(),
    medicineId: z.number().or(z.string()).optional(),
    duration: z.string().optional(),
    durationFormat: z.string().optional(),
    usageType: z.string().min(1, { message: "Please provide usage type." }),
    repeat: z.string().optional(),
    route: z.string().optional(),
    frequency: z.string().optional(),
    quantity: z.string().optional(),
    tradeName: z.string().optional(),
    manufacturer: z.string().optional(),
    packSize: z.string().optional(),
    isManual: z.boolean(),
    note: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.isManual) {
        return data.tradeName && data.tradeName.trim().length > 0;
      }
      return data.medicineId && (data.medicineId !== "" && data.medicineId !== 0);
    },
    {
      message: "Please provide required medication details",
      path: ["medicineId"],
    }
  );

const fetchPrescriptionPdf = async (id, tenantVal, patientId) => {
  const axiosInstance = await createAxiosInstance();
  const response = await axiosInstance.get(
    `${tenantVal}/patientprescription/download/prescription/${id}/patient/${patientId}`,
    {
      responseType: "blob",
    }
  );

  return new Blob([response.data], { type: "application/pdf" });
};

export const downloadPrescription = async (id, tenantVal, patientId) => {
  try {
    const blob = await fetchPrescriptionPdf(id, tenantVal, patientId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `prescription_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("Error downloading prescription");
  }
};

/*
 * Same PDF the download button fetches, but instead of saving it, it's loaded
 * into a hidden iframe and the browser's own print dialog is triggered on it
 * directly — no extra tab, no manual "open the download then print" step.
 */
export const printPrescription = async (id, tenantVal, patientId) => {
  try {
    const blob = await fetchPrescriptionPdf(id, tenantVal, patientId);
    const url = window.URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;

    let hasCleanedUp = false;
    const cleanup = () => {
      if (hasCleanedUp) return;
      hasCleanedUp = true;
      window.URL.revokeObjectURL(url);
      iframe.remove();
    };

    iframe.onload = () => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        cleanup();
        return;
      }

      // The PDF viewer needs a beat to render after the frame's load event
      // fires, or print() opens on a blank page.
      setTimeout(() => {
        frameWindow.addEventListener("afterprint", cleanup, { once: true });
        frameWindow.focus();
        frameWindow.print();
        // Fallback for browsers that never fire afterprint on a frame.
        setTimeout(cleanup, 60000);
      }, 300);
    };

    document.body.appendChild(iframe);
  } catch (error) {
    toast.error("Error printing prescription");
  }
};
